/**
 * Zgjedhja e Llojit të punës (fjalori qendror working_types).
 *
 * Tregon qartë i paguar / i papaguar, ngjyrën dhe kategorinë; liston vetëm
 * tipet AKTIVE — një tip i çaktivizuar nuk hyn në orare të reja, por nëse
 * vlera ekzistuese e mban (histori), shfaqet e shënuar «joaktiv» që të mos
 * humbasë pa u parë. `onChange(code, type)` jep edhe përkufizimin, që forma
 * të tregojë pasojën (paguhet / s’paguhet) pa kërkesë të dytë.
 *
 * ── Pse ka kërkim, dhe pse menyja është e ngushtë ───────────────────────────
 * Fjalori ka njëzet e ca tipe në tetë kategori. Pa kërkim, zgjedhja bëhej duke
 * rrëshqitur një listë sa tërë ekrani, dhe menyja merrte gjerësinë e fushës —
 * mbi një dialog 1200px, emri rrinte majtas dhe çipi i pagesës djathtas, me
 * gjysmë metri bosh mes tyre. Prandaj:
 *
 *   - Menyja ka gjerësi dhe lartësi të vetat (`MENU_W`, `MENU_MAX_H`), pa varësi
 *     nga fusha; hapet POSHTË saj, jo mbi të.
 *   - Kërkimi rri i ngjitur lart dhe filtron emrin, kodin dhe kategorinë.
 *   - Kokat e kategorive janë të ngjitura: kur rrëshqet, e di ku je.
 *   - Rreshtat janë të dendur (`ROW_PY`), sepse një rresht është një emër, një
 *     ngjyrë dhe një çip — jo një kartë.
 *
 * Vlera e zgjedhur mbetet gjithmonë mes fëmijëve edhe kur kërkimi s'e nxjerr;
 * ndryshe MUI-ja ankohet për vlerë jashtë listës dhe fusha zbrazet vetë.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField, MenuItem, Box, Chip, Typography, ListSubheader, InputAdornment } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { authFetch } from '../../services/apiServices';

const CAT = { WORK: 'Punë', MEETING: 'Takim', TRAINING: 'Trajnim', BREAK: 'Pauzë', LEAVE: 'Pushim', HOLIDAY: 'Festë', OVERTIME: 'Orë shtesë', ABSENCE: 'Mungesë', INACTIVE: 'Joaktiv' };
const MENU_W = 380;
const MENU_MAX_H = 340;
const ROW_PY = 0.55;
let cacheAt = 0; let cacheList = [];

export async function fetchWorkingTypes({ all = false } = {}) {
  if (!all && cacheList.length && Date.now() - cacheAt < 60000) return cacheList;
  const r = await authFetch(`/api/working-types${all ? '?all=1' : ''}`);
  const j = await r.json();
  const list = j?.success ? (j.data.types || []) : [];
  if (!all) { cacheList = list; cacheAt = Date.now(); }
  return list;
}

// ACCRUE s'është «pa pagesë»: orët nuk humbin, shkojnë te banka dhe
// shpenzohen si ditë pushimi. Ndaj ka etiketën e vet.
export const PaidChip = ({ paid, accrue = false, size = 'small', sx }) => {
  const [label, bg, fg] = accrue ? ['Akumulohet', '#FEF3C7', '#92400E']
    : paid === false ? ['Pa pagesë', '#FEE2E2', '#991B1B'] : ['Paguhet', '#DCFCE7', '#166534'];
  return (
    <Chip size={size} label={label}
      sx={{ height: 17, fontSize: 9.5, fontWeight: 700, '& .MuiChip-label': { px: .75 }, bgcolor: bg, color: fg, ...sx }} />
  );
};

/** Njësohet ç'shkruhet me ç'kërkohet: pa diakritikë, pa madhësi shkronjash. */
const norm = (s) => String(s || '').toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function WorkingTypeSelect({
  value, onChange, label = 'Lloji i punës', helperText, size = 'small', fullWidth = true,
  categories = null, allowEmpty = true, emptyLabel = 'Parazgjedhja e orarit (PRODUCTIVE)', sx, disabled = false, inputProps,
  // Brenda një rreshti tabele fusha është ~160px: emri, çipi i pagesës dhe
  // shigjeta s'hyjnë dot bashkë, dhe teksti ndihmës e dyfishon lartësinë e
  // rreshtit. Atje mbetet pika e ngjyrës dhe emri, i shkurtuar me elipsë —
  // pagesa lexohet te menyja dhe te fundi i formës, ku ka vend.
  dense = false,
}) {
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState('');
  const searchRef = useRef(null);
  useEffect(() => { let on = true; fetchWorkingTypes().then((l) => { if (on) setTypes(l); }).catch(() => {}); return () => { on = false; }; }, []);
  const list = useMemo(() => {
    const active = types.filter((t) => t.isActive !== false && (!categories || categories.includes(t.category)));
    if (value && !active.some((t) => t.code === value)) active.push({ code: value, name: value, isActive: false, isPaid: null, category: 'INACTIVE', color: '#9CA3AF', _missing: true });
    return active;
  }, [types, categories, value]);

  // Kërkimi prek emrin, kodin dhe kategorinë — dikush e kujton tipin si
  // «pushim», dikush si `SICK_LEAVE`, dhe të dy duhet ta gjejnë.
  const matches = useMemo(() => {
    const needle = norm(q).trim();
    if (!needle) return list;
    return list.filter((t) => norm(`${t.name} ${t.code} ${CAT[t.category] || t.category}`).includes(needle));
  }, [list, q]);
  const shown = useMemo(() => new Set(matches.map((t) => t.code)), [matches]);

  const groups = useMemo(() => {
    const g = new Map();
    for (const t of list) { const k = t.category || 'WORK'; if (!g.has(k)) g.set(k, []); g.get(k).push(t); }
    return [...g.entries()];
  }, [list]);
  const selected = list.find((t) => t.code === value) || null;

  return (
    <TextField select size={size} fullWidth={fullWidth} label={label} value={value || ''} disabled={disabled}
      // `displayEmpty` e vizaton gjithmonë diçka në fushë — qoftë edhe
      // «Parazgjedhja…» — ndaj etiketa duhet të rrijë e ngritur edhe pa vlerë.
      // Ndryshe MUI-ja e mban poshtë dhe të dyja shkruhen mbi njëra-tjetrën.
      InputLabelProps={{ shrink: true }}
      onChange={(e) => onChange(e.target.value || null, list.find((t) => t.code === e.target.value) || null)}
      helperText={dense ? undefined : (helperText ?? (selected ? (selected.isActive === false ? 'Tip i çaktivizuar — mbetet vetëm te historiku; zgjidh një tjetër për orare të reja' : `${CAT[selected.category] || selected.category} · ${selected.isPaid === false ? 'nuk paguhet' : 'paguhet'}`) : undefined))}
      SelectProps={{
        displayEmpty: allowEmpty,
        // Kërkimi merr fokusin vetë; pa këtë MUI-ja e jep te rreshti i zgjedhur
        // dhe shkronja e parë e shtypur shkon te type-ahead-i, jo te fusha.
        autoFocus: false,
        onClose: () => setQ(''),
        MenuProps: {
          anchorOrigin:    { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top',    horizontal: 'left' },
          PaperProps: { sx: { width: MENU_W, maxWidth: '92vw', maxHeight: MENU_MAX_H, borderRadius: 2, mt: .5 } },
          MenuListProps: { dense: true, sx: { pt: 0 } },
          TransitionProps: { onEntered: () => searchRef.current?.focus() },
        },
        renderValue: (v) => {
          const t = list.find((x) => x.code === v);
          if (!t) return <em style={{ color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emptyLabel}</em>;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, minWidth: 0 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '2px', flexShrink: 0, bgcolor: t.color || '#64748B' }} />
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.name}
              </Box>
              {!dense && <PaidChip paid={t.isPaid} accrue={t.payrollImpact === 'ACCRUE'} sx={{ flexShrink: 0 }} />}
            </Box>
          );
        },
      }}
      inputProps={{ 'data-testid': 'working-type-select', ...(inputProps || {}) }} sx={sx}>

      {/* Kërkimi. `ListSubheader` sepse s'është zgjedhje: një `MenuItem` këtu do
          ta mbyllte menynë me çdo klikim brenda fushës. */}
      <ListSubheader sx={{ p: 1, bgcolor: 'background.paper', lineHeight: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          inputRef={searchRef} size="small" fullWidth autoComplete="off"
          placeholder="Kërko lloj pune…" value={q}
          onChange={(e) => setQ(e.target.value)}
          // Select-i e ka type-ahead-in e vet mbi rreshtat; pa këtë, shkrimi te
          // kërkimi do të kërcente edhe përzgjedhjen. Escape mbetet i menysë.
          onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
          inputProps={{ 'data-testid': 'working-type-search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: .5 }}>
                <SearchRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 1.5, fontSize: '0.82rem' },
          }}
        />
      </ListSubheader>

      {allowEmpty && (
        <MenuItem value="" sx={{ py: ROW_PY }} style={q ? { display: 'none' } : undefined}>
          <em style={{ color: '#9CA3AF' }}>{emptyLabel}</em>
        </MenuItem>
      )}

      {groups.flatMap(([cat, items]) => {
        const visible = items.filter((t) => shown.has(t.code));
        return [
          <ListSubheader key={`h-${cat}`}
            style={visible.length ? undefined : { display: 'none' }}
            sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase',
              lineHeight: '24px', color: 'text.secondary', bgcolor: 'background.paper' }}>
            {CAT[cat] || cat}
          </ListSubheader>,
          ...items.map((t) => (
            <MenuItem key={t.code} value={t.code} disabled={t.isActive === false && t.code !== value}
              // Vlera e zgjedhur nuk hiqet kurrë nga fëmijët — vetëm fshihet —
              // që MUI-ja të mos e humbasë lidhjen me të. Fshehja shkon te
              // `style`, jo te `sx`: është gjendje e rreshtit, jo stil i tij.
              style={shown.has(t.code) ? undefined : { display: 'none' }}
              sx={{ gap: 1, py: ROW_PY, minHeight: 0 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: t.color || '#64748B', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ flex: 1, fontSize: '0.82rem', minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.name}{t.isActive === false ? ' (joaktiv)' : ''}
              </Typography>
              <PaidChip paid={t.isPaid} accrue={t.payrollImpact === 'ACCRUE'} />
            </MenuItem>
          )),
        ];
      })}

      {/* Fjalori është i përdoruesit, ndaj rruga për ta zgjeruar rri aty ku
          njeriu sapo kuptoi se i mungon një lloj — jo tri klikime më larg te
          Konfigurimi. Lidhje e thjeshtë hash, sepse ky komponent nuk ka nevojë
          të dijë për router-in për të treguar një rrugë. */}
      <Box sx={{ px: 1, pt: .5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box component="a" href="#/work-types"
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'block', py: .6, fontSize: '0.78rem', fontWeight: 600,
            color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          Menaxho llojet e punës →
        </Box>
      </Box>

      {/* Një kërkim pa përgjigje duhet ta thotë vetë; ndryshe menyja duket e prishur. */}
      {q && matches.length === 0 && (
        <Box sx={{ px: 2, py: 1.5, fontSize: '0.8rem', color: 'text.secondary' }}>
          Asnjë lloj pune për «{q}»
        </Box>
      )}
    </TextField>
  );
}
